import React from 'react';
import { cn } from './cn';

export type CardVariant = 'default' | 'elevated' | 'sunken' | 'hero' | 'outline';
export type CardDensity = 'compact' | 'standard' | 'roomy';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  density?: CardDensity;
  /** Removes the default padding. Pair with `<CardBody>` / `<CardHeader>` for slotted layouts. */
  unpadded?: boolean;
  as?: keyof JSX.IntrinsicElements;
}

const VARIANT_CLASSES: Record<CardVariant, string> = {
  // Default: warm white surface with subtle 1px hairline.
  default:  'bg-surface-1 border border-border-subtle rounded-xl',
  // Elevated: ditto + a whisper of shadow. Use for the first-level content cards.
  elevated: 'bg-surface-1 border border-border-subtle rounded-xl shadow-card',
  // Sunken: recessed panel — use for nested groupings.
  sunken:   'bg-surface-2 border border-border-subtle/60 rounded-xl',
  // Hero: full-bleed, subtle grid overlay, brand-accent gradient edge.
  hero:
    'relative overflow-hidden rounded-2xl border border-border-subtle bg-surface-1 shadow-card',
  // Outline: no fill, border only. Use sparingly.
  outline:  'bg-transparent border border-border rounded-xl',
};

const DENSITY_PADDING: Record<CardDensity, string> = {
  compact:  'p-4',
  standard: 'p-5 md:p-6',
  roomy:    'p-6 md:p-8',
};

export const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant = 'default', density = 'standard', unpadded = false, as: Tag = 'div', className, children, ...rest },
  ref,
) {
  const Component = Tag as unknown as React.ElementType;
  return (
    <Component
      ref={ref as React.Ref<HTMLDivElement>}
      className={cn(
        VARIANT_CLASSES[variant],
        !unpadded && DENSITY_PADDING[density],
        className,
      )}
      {...rest}
    >
      {children}
    </Component>
  );
});

export const CardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className, children, ...rest
}) => (
  <div
    className={cn(
      'px-5 md:px-6 py-4 border-b border-border-subtle flex items-center justify-between gap-4',
      className,
    )}
    {...rest}
  >
    {children}
  </div>
);

export const CardTitle: React.FC<React.HTMLAttributes<HTMLHeadingElement> & { as?: 'h2' | 'h3' | 'h4' }> = ({
  className, children, as: Tag = 'h3', ...rest
}) => (
  <Tag className={cn('text-heading-md text-ink', className)} {...rest}>
    {children}
  </Tag>
);

export const CardEyebrow: React.FC<React.HTMLAttributes<HTMLSpanElement>> = ({
  className, children, ...rest
}) => (
  <span
    className={cn('text-overline uppercase text-ink-subtle', className)}
    {...rest}
  >
    {children}
  </span>
);

export const CardBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className, children, ...rest
}) => (
  <div className={cn('px-5 md:px-6 py-5', className)} {...rest}>
    {children}
  </div>
);

export const CardFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className, children, ...rest
}) => (
  <div
    className={cn(
      'px-5 md:px-6 py-4 border-t border-border-subtle flex items-center justify-end gap-3',
      className,
    )}
    {...rest}
  >
    {children}
  </div>
);
